import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Eye, EyeOff, ShieldAlert, ShieldCheck, Loader2 } from "lucide-react";
import { z } from "zod";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { checkPwnedPassword } from "@/lib/hibp.functions";
import { normalizePhoneDigits, isValidContactPhone } from "@/lib/phone";

export const Route = createFileRoute("/signup")({
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/signup" },
      { title: "Εγγραφή — MrPanda.gr" },
      { name: "description", content: "Δημιούργησε λογαριασμό στο MrPanda.gr για ταχύτερες αγορές, παρακολούθηση παραγγελιών και αποκλειστικές προσφορές." },
      { name: "robots", content: "noindex, follow" },
    ] }),
  component: SignupPage,
});

const signupSchema = z.object({
  firstName: z.string().trim().min(1, "Το όνομα είναι υποχρεωτικό").max(60, "Πολύ μεγάλο όνομα"),
  lastName: z.string().trim().min(1, "Το επώνυμο είναι υποχρεωτικό").max(60, "Πολύ μεγάλο επώνυμο"),
  email: z.string().trim().email("Μη έγκυρο email").max(255),
  phone: z
    .string()
    .trim()
    .refine(
      (v) => isValidContactPhone(v),
      "Δώσε τηλέφωνο Ελλάδας (10 ψηφία) ή Κύπρου (8 ψηφία, π.χ. 99123456)",
    ),
  password: z
    .string()
    .min(8, "Τουλάχιστον 8 χαρακτήρες")
    .regex(/[a-z]/, "Χρειάζεται ένα πεζό γράμμα")
    .regex(/[A-Z]/, "Χρειάζεται ένα κεφαλαίο γράμμα")
    .regex(/[0-9]/, "Χρειάζεται έναν αριθμό"),
  terms: z.literal(true, {
    message: "Πρέπει να αποδεχτείς τους όρους για να συνεχίσεις",
  }),
});

function passwordScore(pw: string) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[a-z]/.test(pw)) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

function SignupPage() {
  const nav = useNavigate();
  const checkPwned = useServerFn(checkPwnedPassword);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pwned, setPwned] = useState<"idle" | "checking" | "safe" | "leaked">("idle");
  const pwnedCount = useRef(0);

  const score = useMemo(() => passwordScore(password), [password]);
  const strength = ["Πολύ αδύναμος", "Αδύναμος", "Μέτριος", "Καλός", "Δυνατός", "Πολύ δυνατός"][score];

  // Debounced "leaked password" check via Have I Been Pwned.
  useEffect(() => {
    if (password.length < 8) {
      setPwned("idle");
      return;
    }
    setPwned("checking");
    const current = password;
    const t = setTimeout(async () => {
      try {
        const out = await checkPwned({ data: { password: current } });
        if (current !== password) return; // stale
        pwnedCount.current = out.count;
        setPwned(out.pwned ? "leaked" : "safe");
      } catch {
        setPwned("idle"); // fail open on network errors
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setMsg(null); setErrors({});
    const parsed = signupSchema.safeParse({ firstName, lastName, email, phone, password, terms });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) fieldErrors[issue.path[0] as string] = issue.message;
      setErrors(fieldErrors);
      return;
    }
    if (pwned === "leaked") {
      setErrors((prev) => ({
        ...prev,
        password: "Αυτός ο κωδικός έχει εμφανιστεί σε διαρροές δεδομένων. Διάλεξε άλλον.",
      }));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/public/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: parsed.data.firstName,
          lastName: parsed.data.lastName,
          email: parsed.data.email,
          phone: parsed.data.phone,
          password: parsed.data.password,
          terms: parsed.data.terms,
        }),
      });
      const out = (await res.json().catch(() => ({}))) as { error?: string };
      setLoading(false);
      if (!res.ok) {
        setErr(out.error || "Δεν ήταν δυνατή η εγγραφή. Δοκίμασε ξανά.");
        return;
      }
      setMsg("Έλεγξε το email σου για επιβεβαίωση. 🐼");
      setTimeout(() => nav({ to: "/login" }), 2600);
    } catch {
      setLoading(false);
      setErr("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
    }
  };

  const inputCls = (field: string) =>
    `mt-1 h-10 w-full rounded-md border bg-background px-3 ${errors[field] ? "border-destructive" : "border-border"}`;

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-md px-4 py-10">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="grid h-20 w-20 place-items-center rounded-full bg-accent text-5xl animate-bob">🐼</div>
          <h1 className="mt-3 text-2xl font-bold">Γίνε μέλος της παρέας!</h1>
          <p className="mt-1 text-sm text-muted-foreground">Λίγα στοιχεία και είσαι έτοιμος για ψώνια 🛍️</p>
        </div>
        <form onSubmit={submit} noValidate className="animate-pop-in space-y-3 rounded-2xl border border-border p-6 shadow-sm mp-playful-panel">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              😊 Όνομα <span className="text-destructive">*</span>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputCls("firstName")} />
              {errors.firstName && <span className="mt-1 block text-xs text-destructive">{errors.firstName}</span>}
            </label>
            <label className="block text-sm">
              👤 Επώνυμο <span className="text-destructive">*</span>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputCls("lastName")} />
              {errors.lastName && <span className="mt-1 block text-xs text-destructive">{errors.lastName}</span>}
            </label>
          </div>
          <label className="block text-sm">
            📧 Email <span className="text-destructive">*</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" inputMode="email" className={inputCls("email")} />
            {errors.email && <span className="mt-1 block text-xs text-destructive">{errors.email}</span>}
          </label>
          <label className="block text-sm">
            📱 Αριθμός κινητού τηλεφώνου <span className="text-destructive">*</span>
            <input value={phone} onChange={(e) => setPhone(normalizePhoneDigits(e.target.value))} type="tel" inputMode="numeric" maxLength={12} placeholder="π.χ. 6912345678 ή 99123456" className={inputCls("phone")} />
            {errors.phone && <span className="mt-1 block text-xs text-destructive">{errors.phone}</span>}
          </label>
          <label className="block text-sm">
            🔒 Κωδικός πρόσβασης <span className="text-destructive">*</span>
            <div className="relative mt-1">
              <input value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? "text" : "password"} className={`h-10 w-full rounded-md border bg-background px-3 pr-10 ${errors.password ? "border-destructive" : "border-border"}`} />
              <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Απόκρυψη κωδικού" : "Προβολή κωδικού"} className="absolute inset-y-0 right-0 grid w-10 place-items-center text-muted-foreground hover:text-foreground">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {password && (
              <div className="mt-2">
                <div className="flex gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span
                      key={i}
                      className={`h-1.5 flex-1 rounded-full ${i < score ? (score <= 2 ? "bg-destructive" : score === 3 ? "bg-yellow-500" : "bg-success") : "bg-muted"}`}
                    />
                  ))}
                </div>
                <span className="mt-1 block text-xs text-muted-foreground">Ασφάλεια: {strength}</span>
              </div>
            )}
            {password.length >= 8 && pwned === "checking" && (
              <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Έλεγχος για διαρροές...
              </span>
            )}
            {pwned === "leaked" && (
              <span className="mt-1 flex items-center gap-1 text-xs text-destructive">
                <ShieldAlert className="h-3 w-3" /> Βρέθηκε σε διαρροές δεδομένων — διάλεξε άλλον κωδικό.
              </span>
            )}
            {pwned === "safe" && (
              <span className="mt-1 flex items-center gap-1 text-xs text-success">
                <ShieldCheck className="h-3 w-3" /> Δεν βρέθηκε σε γνωστές διαρροές.
              </span>
            )}
            {errors.password && <span className="mt-1 block text-xs text-destructive">{errors.password}</span>}
            {!errors.password && (
              <span className="mt-1 block text-xs text-muted-foreground">
                Τουλάχιστον 8 χαρακτήρες, με πεζά, κεφαλαία και αριθμούς.
              </span>
            )}
          </label>
          <div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 h-4 w-4 shrink-0 rounded border-border" />
              <span>
                Συμφωνώ με τους{" "}
                <Link to="/terms" className="text-primary hover:underline">όρους χρήσης</Link> και την{" "}
                <Link to="/privacy" className="text-primary hover:underline">πολιτική απορρήτου</Link> και δηλώνω ότι είμαι τουλάχιστον 15 ετών ή έχω τη συναίνεση γονέα/κηδεμόνα <span className="text-destructive">*</span>
              </span>
            </label>
            {errors.terms && <span className="mt-1 block text-xs text-destructive">{errors.terms}</span>}
          </div>
          {err && <div className="rounded bg-destructive/10 p-2 text-sm text-destructive">{err}</div>}
          {msg && <div className="rounded bg-success/10 p-2 text-sm text-success">{msg}</div>}
          <button disabled={loading || pwned === "leaked"} type="submit" className="mp-bouncy w-full rounded-full bg-primary py-2.5 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {loading ? "Εγγραφή..." : "Εγγραφή 🎉"}
          </button>
          <div className="text-center text-sm text-muted-foreground">Έχεις ήδη λογαριασμό; <Link to="/login" className="text-primary hover:underline">Σύνδεση</Link></div>
        </form>
      </div>
    </StorefrontLayout>
  );
}
